import { IAddress } from './common.dto';
import { IAdminUser, ILicense } from './crm-user';
import { ICustomerUser } from './customer.dto';
import { UploadFileResponse } from './file.dto';

export const mitteTypeList = [
  'pack',
  'pills',
  'cocktail',
  'gram',
  'applicator',
  'patch',
  'milliliter',
  'capsule',
  'tablet',
  'troche',
  'strip',
  'pump',
  'each',
  'transmitter',
  'sensor',
  'receiver',
  'reader',
  'sensors',
  'vial',
  'inhaler',
  'lancets',
  'needles',
  'tips',
  'syringes',
  'tube',
  'patches',
  'swabs',
  'strips',
  'meter',
  'pens',
  'bottle',
  'tabs',
  'container',
  'mixture',
  'other'
] as const;
export type MitteType = (typeof mitteTypeList)[number] | string;

export type CocktailOptions = {
  din: string;
  mitte: number;
  type: 'one-time' | 'subscription';
};

export type PackOptions = {
  retailPrice: number;
  mitte: number;
  type: 'one-time' | 'subscription';
};
export class RxDrug {
  din: string;
  ndc?: string;
  drugName?: string;
  strength?: string;
  mitte: number;
  daysSupply?: number;
  refills: number;
  notes: string;
  mitteType?: MitteType;
  /** for 'pack' mitteType */
  options?: PackOptions[] | CocktailOptions[];
  rxId?: string; // this external id, only used for prescription created by beluga;,
  pharmacyNotes?: string;
}

export type DrugTitrationType =
  | 'biweekly'
  | 'standard'
  | 'alternative'
  | 'rapid';

export const WeightlossCategoryMap = {
  semaglutide: {
    Weightloss1: 0.25,
    Weightloss2: 0.5,
    Weightloss3: 1,
    Weightloss4: 1.7,
    Weightloss5: 2,
    Weightloss6: 2.4
  },
  tirzepatide: {
    Weightloss1: 2.5,
    Weightloss2: 5,
    Weightloss3: 7.5,
    Weightloss4: 10,
    Weightloss5: 12.5,
    Weightloss6: 15
  }
};
export type WeightlossDoseCategories =
  | keyof (typeof WeightlossCategoryMap)['tirzepatide']
  | 'N/A';
export class Drug {
  _id: string;
  din: string;
  ndc?: string;
  name: string;
  mitteType: MitteType;
  organization: string;
  /** for 'pack' mitteType */
  options?: (PackOptions | CocktailOptions)[];
  products: string[];
  price?: number;
  sig?: string;
  /** Quantity */
  mitte?: number;
  strength?: string;
  refills?: number;
  /**
   * Pharmacy `_id` (new pharmacy or legacy CRM-user). Legacy rows may still
   * store Beluga pharmacy id (`belugaId`).
   */
  pharmacy?: string;
  extras?: DrugExtras;
  status: DrugStatus;
  productDetails?: { _id: string; label: string; subLabel?: string }[];
}

export type WeightlossDosageLevel = 'titrating' | 'maintenance';

export class PharmetikaDrugExtras {
  id?: string;
  drugName?: string;
  drugStrength?: string;
  quantity?: string;
  daySupply?: string;
  quantityUnit?: 'ml' | 'mg' | 'vial';
  pharmacyNotePassed?: boolean;
  reasonForCompounding?: {
    description?: string;
    usePharmacyNoteAsContext?: boolean;
    context?: string;
  };
  [x: string]: any;
}
export class LifeFileDrugExtras {
  drugName?: string;
  drugStrength?: string;
  drugForm?: string;
  productId?: number;
  quantityUnits?: string;
  packSize?: string;
  [x: string]: any;
}

/** One OptioRx product a drug maps to; each entry is submitted as its own prescription. */
export class OptioRxDrugMapping {
  /** OptioRx `item_alternate_id`. Falls back to the drug's `ndc` when unset. */
  itemAlternateId?: string;
  /**
   * Sent as OptioRx's `rx_unit` (their package size, e.g. `vial`, `each`),
   * overriding `mitteType`. Our catalogue's own vocabulary — `milliliter`,
   * `troche`, `pills` — is not OptioRx's, and they publish no accepted list, so
   * this is where the value they do accept goes. Left unset, `mitteType` is sent
   * as-is.
   */
  rxUnit?: string;
  /** OptioRx `item`. Falls back to the prescribed name and strength. */
  drugName?: string;
  /** OptioRx `rx_quantity`. Falls back to the fulfilled quantity. */
  drugQuantity?: string | number;
  reasonForCompounding?: {
    description?: string;
    usePharmacyNoteAsContext?: boolean;
    context?: string;
  };
  [x: string]: any;
}

export class DrugExtras {
  drugTitration?: DrugTitrationType;
  category?: WeightlossDoseCategories;
  dosageType?: WeightlossDosageLevel;
  additive?: string[];
  visitType?: any; //EVisitType; // TODO: fix this, should not import from lib/data
  lifeFile?: LifeFileDrugExtras;
  pharmetika?: PharmetikaDrugExtras;
  /** Older data may hold a single object; the OptioRx middleware reads both. */
  optiorx?: OptioRxDrugMapping[];
  previousDins?: string[];
  /**
   * Used as a flag to identify that the Weightloss Drug is meant to be a starting dose for the first cycle of a customers prescription.
   */
  isStarter?: boolean;
  createOrderFromRx?: boolean;
  [x: string]: any;
}

export class DrugDTO {
  din: string;
  ndc?: string;
  name?: string;
  mitteType?: MitteType;
  /** for 'pack' mitteType */
  options?: (PackOptions | CocktailOptions)[];
  price?: number;
  sig?: string;
  mitte?: number;
  refills?: number;
  strength?: string;
  products?: string[];
  categories?: string[];
  status: DrugStatus;
  /**
   * Pharmacy `_id` (new pharmacy or legacy CRM-user). Legacy rows may still
   * store Beluga pharmacy id (`belugaId`). Null clears the field.
   */
  pharmacy?: string | null;
  extras?: any;
}

export type DrugStatus = 'active' | 'disabled';

export class FulfillDrug {
  din: string;
  qty: number;
  name: string;
  mitte: number;
  refills: number;
  txNumber?: string;
}

export class Fulfillment {
  createdAt: Date;
  orderId: number;
  status: 'new' | 'pending' | 'completed' | 'sent' | 'send_failed';
  drugs: FulfillDrug[];
  notes: string;
  updatedBy?: string | IAdminUser;
  sentTo?: string;
}
export class PrescriptionExtras {}

export class PrescriptionDTO {
  customer: string;
  province?: string;
  createdBy?: string;
  authorizedBy?: string;
  createdAt?: string;
  drugs?: RxDrug[];
  notes?: string;
  fulfillments?: Fulfillment[];
  isVerbal?: boolean;
  /**
   * Explicit EMR order link (indexed). Used for transfer workflows: staff-linked Rx,
   * or set when an order is created from the Rx→order link flow. Query with
   * `PrescriptionsQuery.linkedOrderId`. Not the same as `meta.fromOrderId`.
   */
  linkedOrderId?: string;
  meta?: PrescriptionMeta;
  extras?: PrescriptionExtras;
  updatedBy?: string | IAdminUser;
  pdfKey?: string;
}

export class UpdatePrescriptionDTO {
  customer?: string;
  createdBy?: string;
  authorizedBy?: string;
  createdAt?: string;
  drugs?: RxDrug[];
  notes?: string;
  fulfillments?: Fulfillment[];
  voided?: boolean;
  voidedAt?: Date;
  transferredOutAt?: Date;
  transferredOutBy?: string | IAdminUser;
  transferredOutMethod?: 'chat_pdf' | 'fax' | 'manual';
  printCounter?: number;
  linkedOrderId?: string | null;
  meta?: PrescriptionMeta;
  extras?: any;
  updatedBy?: string | IAdminUser;
  pdfKey?: string;
}

export type PrescriptionMeta = {
  source?: string,
  docFirstName?: string,
  docLastName?: string,
  docNPI?: string,
  credential?: string,
  phone?: string,
  address1?: string,
  address2?: string,
  city?: string,
  state?: string,
  zip?: string,
  orderId?: number,
  pdfData?: UploadFileResponse
  rxNumber?: string;
  /**
   * Set when the Rx is created from the EMR while viewing a non-digital order.
   * Hints fulfillment / “order for this Rx” logic. Does not imply `linkedOrderId`
   * (transfer binding); those are separate concerns.
   */
  fromOrderId?: string | number;
  /**
   * Pharmacy that owns `pharmacyPrescriptionId`. Blend writes `'blend'` on submit / heal.
   */
  pharmacySource?: 'blend';
  /**
   * Blend electronic prescription UUID. Written after the first Blend composite
   * submission and reused on later fills so we skip creating a new e-Rx.
   */
  pharmacyPrescriptionId?: string;
  [x: string]: any
} & any

export class Prescription {
  _id?: string;
  customer?: string | ICustomerUser;
  createdBy?: string | IAdminUser;
  authorizedBy?: string;
  drugs: RxDrug[];
  notes?: string;
  fulfillments?: Fulfillment[];
  updatedAt?: Date;
  createdAt?: Date;
  printCounter: number;
  prescriberName?: string;
  voidedAt?: Date;
  transferredOutAt?: Date;
  transferredOutBy?: string | IAdminUser;
  transferredOutMethod?: 'chat_pdf' | 'fax' | 'manual';
  organization?: string;
  /** See `PrescriptionDTO.linkedOrderId`. */
  linkedOrderId?: string;
  meta?: PrescriptionMeta;
  extras?: any;
  updatedBy?: string | IAdminUser;
  pdfKey?: string;
}

export class IPrescription extends Prescription { }

export class IPrescriptionPopulated extends Prescription {
  declare createdBy?: IAdminUser;
  declare customer?: ICustomerUser;
  customerName?: string;
  declare prescriberName?: string;
}

export class PrescriptionExt extends Prescription {
  lastFulfill?: Fulfillment;
  expand?: boolean;
}

export class PrescriptionsQuery {
  maxId?: string;
  minId?: string;
  din?: string;
  dins?: string[];
  customerId?: string;
  includeVoided?: boolean;
  customerName?: string;
  meta?: Partial<PrescriptionMeta>;
  prescriberName?: string;
  includeFulfilled?: boolean;
  /** Match prescriptions that have a fulfillment for this order (`fulfillments.orderId`). */
  orderId?: string;
  /** Match prescriptions linked to this EMR order (`linkedOrderId` on the document). */
  linkedOrderId?: string;
  maxCreatedAt?: Date | string;
  minCreatedAt?: Date | string;
  rxId?: string;
  // Server-side (offset) pagination.
  limit?: number;
  offset?: number;
  includeCount?: boolean;
  sortField?: string;
  sortOrder?: 1 | -1;
}

export class IGetDrugsDTO {
  din?: string;
  dins?: string[];
  ndc?: string;
  productId?: string;
  name?: string;
  dinName?: string;
  pharmacy?: string;
  mitteType?: string;
  mitte?: number;
  refills?: number;
  strength?: string;
  status?: string;
  // Server-side (offset) pagination.
  limit?: number;
  offset?: number;
  includeCount?: boolean;
  sortField?: string;
  sortOrder?: 1 | -1;
}

export class IPrescriptionTemplateData {
  clinicName: string;
  provider: {
    firstName: string;
    lastName: string;
    docNPI: string;
    label?: string;
    credential?: string;
    _id?: string;
    licenses?: ILicense[];
  };
  signature?: string;
  clinic: {
    address1: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
  };
  phone: string;
  fax: string;
  customer: {
    firstName: string;
    lastName: string;
    dob: string;
    defaultAddress: IAddress;
    allergies: string;
    medicationHistory: string;
    medicalConditions?: string;
    hcNumber?: string;
    phone?: string;
  };
  createdAt: string;
  drugs: Array<{
    drugName: string;
    strength: string;
    mitte: string;
    mitteType: string;
    notes: string;
    refills: string;
    din?: string;
  }>;
  notes?: string;
  /** Insurance from drug card consult (e.g. for second page of JJC prescription PDF) */
  insurance?: Array<{
    planName: string;
    type: string;
    /** All drug card info fields (group number, certificate, carrier, discount card, etc.) with display labels */
    infoFields: Array<{ label: string; value: string }>;
    nameOnCard: string;
    relationship: string;
  }>;
  /** Patient government ID photo (front) as data URL, e.g. from Persona file `Persona-govIdFront.jpg` */
  governmentIdPhoto?: string;
  /** Patient government ID photo (back) as data URL, e.g. from Persona file `Persona-govIdBack.jpg` */
  governmentIdPhotoBack?: string;
}

export type OrderPrescriptionType = 'fulfilled' | 'v1' | 'related' | 'otc';
